import React, { useState } from 'react';
import {
  FaCalendarAlt, FaSpinner, FaChartBar, FaSearch,
  FaCheckCircle, FaFileAlt, FaPrint, FaEuroSign, FaDollarSign, FaWonSign, FaPoundSign, FaPiggyBank, FaChartLine
} from 'react-icons/fa';
import { useNotification } from '../../contexts/NotificationContext';
import { newFinanceService, formatCurrencyTotals } from '../../services/newFinance.service';
import { carryoverService } from '../../services/carryover.service';
import type { FinanceRecord, CurrencyTotals } from '../../types/newFinance';
import { EXPENSE_CATEGORY_LABELS } from '../../types/newFinance';
import type { CurrencyBalances } from '../../types/carryover';
import { MonthlyFinanceCharts } from './MonthlyFinanceCharts';

interface MonthlySummary {
  month: number;
  income: CurrencyTotals;
  expense: CurrencyTotals;
  net: CurrencyTotals;
}

interface MonthlyReportData {
  period: {
    year: number;
    month: number; // 0 if annual
    startDate: string;
    endDate: string;
  };
  summary: {
    totalIncome: CurrencyTotals;
    totalExpense: CurrencyTotals;
    netIncome: CurrencyTotals;
    recordCount: number;
  };
  records: FinanceRecord[];
  categoryBreakdown: {
    income: {
      tithe: CurrencyTotals;
      thanksgiving: CurrencyTotals;
      other: CurrencyTotals;
    };
    expense: Record<string, CurrencyTotals>;
  };
  // 연간 이월금 (1월 기준점)
  annualCarryover?: CurrencyBalances;
  // 전월 이월금
  previousBalance: CurrencyTotals;
  // 월말 잔액
  endingBalance: CurrencyTotals;
  // 연간 요약 데이터 (선택적)
  isAnnualSummary?: boolean;
  monthlySummaries?: MonthlySummary[];
}

export const MonthlyReport: React.FC = () => {
  const { addNotification } = useNotification();

  // 상태 관리
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [reportData, setReportData] = useState<MonthlyReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCharts, setShowCharts] = useState(false);

  // 년도/월 옵션 생성
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i + 2);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  const generateReport = async () => {
    setLoading(true);
    try {
      // 승인된 재정 기록만 조회
      const allRecords = await newFinanceService.getFinanceRecordsByStatus('approved');

      let startDate = '';
      let endDate = '';

      if (selectedMonth === 0) {
        // 전체 (연간) 조회
        startDate = `${selectedYear}-01-01`;
        endDate = `${selectedYear}-12-31`;
      } else {
        // 월별 조회
        startDate = `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}-01`;
        // 타임존 문제 해결: 로컬 시간 기준으로 마지막 날짜 계산
        const lastDay = new Date(selectedYear, selectedMonth, 0);
        endDate = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
      }

      console.log(`Report Period: ${startDate} ~ ${endDate}`); // 디버깅용

      const targetRecords = allRecords.filter(record =>
        record.date >= startDate && record.date <= endDate
      );

      if (targetRecords.length === 0) {
        addNotification('해당 기간에 승인된 재정 데이터가 없습니다.', 'info');
        setReportData(null);
        return;
      }

      // 합계 계산
      const totalIncome: CurrencyTotals = { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 };
      const totalExpense: CurrencyTotals = { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 };

      // 카테고리별 분석
      const incomeBreakdown = {
        tithe: { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 },
        thanksgiving: { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 },
        other: { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 }
      };

      const expenseBreakdown: Record<string, CurrencyTotals> = {};
      Object.keys(EXPENSE_CATEGORY_LABELS).forEach(category => {
        expenseBreakdown[category] = { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 };
      });

      // 월별 집계 (연간 요약용)
      const monthlySummaries: MonthlySummary[] = [];
      if (selectedMonth === 0) {
        for (let m = 1; m <= 12; m++) {
          monthlySummaries.push({
            month: m,
            income: { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 },
            expense: { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 },
            net: { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 }
          });
        }
      }

      targetRecords.forEach(record => {
        // 수입 합계
        Object.keys(totalIncome).forEach(currency => {
          totalIncome[currency as keyof CurrencyTotals] += record.incomeTotals[currency as keyof CurrencyTotals];
          totalExpense[currency as keyof CurrencyTotals] += record.expenseTotals[currency as keyof CurrencyTotals];
        });

        // 수입 카테고리별
        record.titheItems.forEach(item => {
          incomeBreakdown.tithe[item.currency as keyof CurrencyTotals] += item.amount;
        });
        record.thanksgivingItems.forEach(item => {
          incomeBreakdown.thanksgiving[item.currency as keyof CurrencyTotals] += item.amount;
        });
        record.otherIncomeItems.forEach(item => {
          incomeBreakdown.other[item.currency as keyof CurrencyTotals] += item.amount;
        });

        // 지출 카테고리별
        record.expenseItems.forEach(item => {
          expenseBreakdown[item.category][item.currency as keyof CurrencyTotals] += item.amount;
        });

        // 연간 요약일 경우 월별 집계 추가
        if (selectedMonth === 0) {
          const recordMonth = new Date(record.date).getMonth() + 1;
          const summaryIndex = recordMonth - 1;
          if (monthlySummaries[summaryIndex]) {
            Object.keys(totalIncome).forEach(curr => {
              const c = curr as keyof CurrencyTotals;
              monthlySummaries[summaryIndex].income[c] += record.incomeTotals[c];
              monthlySummaries[summaryIndex].expense[c] += record.expenseTotals[c];
              monthlySummaries[summaryIndex].net[c] += record.netTotals[c];
            });
          }
        }
      });

      // 순손익 계산
      const netIncome: CurrencyTotals = {
        HUF: totalIncome.HUF - totalExpense.HUF,
        EUR: totalIncome.EUR - totalExpense.EUR,
        USD: totalIncome.USD - totalExpense.USD,
        KRW: totalIncome.KRW - totalExpense.KRW,
        GBP: totalIncome.GBP - totalExpense.GBP,
        CAD: totalIncome.CAD - totalExpense.CAD
      };

      // 연간 이월금 조회
      let annualCarryover: CurrencyBalances | undefined;
      try {
        const carryoverData = await carryoverService.getAnnualCarryover(selectedYear);
        if (carryoverData) {
          annualCarryover = carryoverData.balances;
        }
      } catch (e) {
        console.log('연간 이월금 없음:', selectedYear);
      }

      // 전월 이월금 계산 (연간이월 + 1월~전월까지의 순수익 합계)
      const previousBalance: CurrencyTotals = { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 };

      // 연간 이월금을 기본값으로 설정
      if (annualCarryover) {
        previousBalance.HUF = annualCarryover.HUF || 0;
        previousBalance.EUR = annualCarryover.EUR || 0;
        previousBalance.USD = annualCarryover.USD || 0;
        previousBalance.KRW = annualCarryover.KRW || 0;
        previousBalance.GBP = annualCarryover.GBP || 0;
        previousBalance.CAD = annualCarryover.CAD || 0;
      }

      // 월별 조회일 경우 2월 이후면 전월까지의 순수익 합산
      if (selectedMonth > 1) {
        const yearStart = `${selectedYear}-01-01`;
        const prevMonthLastDay = new Date(selectedYear, selectedMonth - 1, 0);
        const prevMonthEnd = `${prevMonthLastDay.getFullYear()}-${String(prevMonthLastDay.getMonth() + 1).padStart(2, '0')}-${String(prevMonthLastDay.getDate()).padStart(2, '0')}`;

        const previousMonthsRecords = allRecords.filter(record =>
          record.date >= yearStart && record.date <= prevMonthEnd
        );

        previousMonthsRecords.forEach(record => {
          previousBalance.HUF += (record.incomeTotals.HUF - record.expenseTotals.HUF);
          previousBalance.EUR += (record.incomeTotals.EUR - record.expenseTotals.EUR);
          previousBalance.USD += (record.incomeTotals.USD - record.expenseTotals.USD);
          previousBalance.KRW += (record.incomeTotals.KRW - record.expenseTotals.KRW);
          previousBalance.GBP += ((record.incomeTotals.GBP || 0) - (record.expenseTotals.GBP || 0));
          previousBalance.CAD += ((record.incomeTotals.CAD || 0) - (record.expenseTotals.CAD || 0));
        });
      }

      // 월말 잔액 계산 (전월이월 + 당월/연간 순수익)
      const endingBalance: CurrencyTotals = {
        HUF: previousBalance.HUF + netIncome.HUF,
        EUR: previousBalance.EUR + netIncome.EUR,
        USD: previousBalance.USD + netIncome.USD,
        KRW: previousBalance.KRW + netIncome.KRW,
        GBP: previousBalance.GBP + netIncome.GBP,
        CAD: previousBalance.CAD + netIncome.CAD
      };

      setReportData({
        period: {
          year: selectedYear,
          month: selectedMonth,
          startDate,
          endDate
        },
        summary: {
          totalIncome,
          totalExpense,
          netIncome,
          recordCount: targetRecords.length
        },
        records: targetRecords,
        categoryBreakdown: {
          income: incomeBreakdown,
          expense: expenseBreakdown
        },
        annualCarryover,
        previousBalance,
        endingBalance,
        isAnnualSummary: selectedMonth === 0,
        monthlySummaries: selectedMonth === 0 ? monthlySummaries : undefined
      });

      addNotification('보고서가 생성되었습니다.', 'success');
      setShowCharts(true);
    } catch (error) {
      console.error('보고서 생성 오류:', error);
      addNotification('보고서 생성 중 오류가 발생했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getCurrencyIcon = (currency: string) => {
    switch (currency) {
      case 'EUR': return <FaEuroSign className="h-3 w-3" />;
      case 'USD': return <FaDollarSign className="h-3 w-3" />;
      case 'KRW': return <FaWonSign className="h-3 w-3" />;
      case 'GBP': return <FaPoundSign className="h-3 w-3" />;
      case 'CAD': return <span className="text-[10px] font-bold">CA$</span>;
      default: return <span className="text-xs">Ft</span>;
    }
  };

  const renderCurrencyTotals = (totals: CurrencyTotals, colorClass: string = '') => {
    const nonZero = Object.entries(totals).filter(([_, amount]) => amount !== 0);
    if (nonZero.length === 0) return <span className={colorClass}>0 HUF</span>;

    return (
      <div className={`space-y-1 ${colorClass}`}>
        {nonZero.map(([currency, amount]) => (
          <div key={currency} className="flex items-center">
            {getCurrencyIcon(currency)}
            <span className="ml-1">{amount.toLocaleString()} {currency}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
      {/* 헤더 */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <FaFileAlt className="h-5 w-5 mr-2 text-blue-500" />
              📊 월별 재정 보고서
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              승인된 재정 데이터를 기반으로 한 월간 분석 보고서
            </p>
          </div>

          {/* 기간 선택 및 생성 버튼 */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex items-center space-x-2">
              <FaCalendarAlt className="h-4 w-4 text-gray-400" />
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}년</option>
                ))}
              </select>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value={0}>전체 (연간)</option>
                {months.map(month => (
                  <option key={month} value={month}>{month}월</option>
                ))}
              </select>
            </div>

            <button
              onClick={generateReport}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white rounded-lg transition-colors"
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  생성 중...
                </>
              ) : (
                <>
                  <FaSearch className="mr-2" />
                  보고서 생성
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* 보고서 내용 */}
      <div className="p-6">
        {!reportData ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <FaChartBar className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>보고서를 생성하려면 기간을 선택하고 "보고서 생성" 버튼을 클릭하세요.</p>
            <p className="text-sm mt-2">승인된 재정 데이터만 보고서에 포함됩니다.</p>
          </div>
        ) : (
          <div className="space-y-8 animate-fadeIn">
            {/* 리포트 헤더 */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                  {reportData.period.year}년 {reportData.period.month === 0 ? '통합' : `${reportData.period.month}월`} 재정 보고서
                  <span className="ml-3 px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center">
                    <FaCheckCircle className="mr-1" /> 작성 완료
                  </span>
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  기간: {reportData.period.startDate} ~ {reportData.period.endDate}
                </p>
              </div>
              <div className="mt-4 md:mt-0 flex space-x-2">
                {!reportData.isAnnualSummary && (
                  <button
                    onClick={() => setShowCharts(!showCharts)}
                    className={`px-4 py-2 ${showCharts ? 'bg-purple-600' : 'bg-purple-500'} hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center`}
                  >
                    <FaChartLine className="mr-2" />
                    {showCharts ? '차트 숨기기' : '차트 보기'}
                  </button>
                )}
                <button
                  onClick={handlePrint}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center"
                >
                  <FaPrint className="mr-2" />
                  인쇄
                </button>
              </div>
            </div>

            {/* 요약 카드 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border-l-4 border-green-500 ring-1 ring-gray-100 dark:ring-gray-700">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                  {reportData.isAnnualSummary ? '연간 총 수입' : '총 수입'}
                </h3>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {renderCurrencyTotals(reportData.summary.totalIncome)}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border-l-4 border-red-500 ring-1 ring-gray-100 dark:ring-gray-700">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                  {reportData.isAnnualSummary ? '연간 총 지출' : '총 지출'}
                </h3>
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {renderCurrencyTotals(reportData.summary.totalExpense)}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border-l-4 border-blue-500 ring-1 ring-gray-100 dark:ring-gray-700">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                  {reportData.isAnnualSummary ? '연간 순손익' : '순이익 (당월)'}
                </h3>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {renderCurrencyTotals(reportData.summary.netIncome)}
                </div>
              </div>
            </div>

            {/* 차트 섹션 (월별보고서일 때만) */}
            {showCharts && !reportData.isAnnualSummary && (
              <div className="print-only">
                <MonthlyFinanceCharts
                  records={reportData.records}
                  selectedMonth={selectedMonth}
                />
              </div>
            )}

            {/* 연간 요약일 경우: 월별 요약 테이블 */}
            {reportData.isAnnualSummary && reportData.monthlySummaries ? (
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">📅 월별 요약</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                    <thead className="bg-gray-100 dark:bg-gray-600">
                      <tr>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">월</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">수입</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">지출</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">순손익</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600 bg-white dark:bg-gray-800">
                      {reportData.monthlySummaries.map((summary) => (
                        <tr key={summary.month} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-4 py-3 text-center text-sm font-medium text-gray-900 dark:text-white">
                            {summary.month}월
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-green-600 dark:text-green-400">
                            {renderCurrencyTotals(summary.income)}
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-red-600 dark:text-red-400">
                            {renderCurrencyTotals(summary.expense)}
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-blue-600 dark:text-blue-400 font-bold">
                            {renderCurrencyTotals(summary.net)}
                          </td>
                        </tr>
                      ))}
                      {/* 합계 행 */}
                      <tr className="bg-gray-100 dark:bg-gray-700 font-bold">
                        <td className="px-4 py-3 text-center text-sm text-gray-900 dark:text-white">합계</td>
                        <td className="px-4 py-3 text-right text-sm text-green-700 dark:text-green-300">
                          {renderCurrencyTotals(reportData.summary.totalIncome)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-red-700 dark:text-red-300">
                          {renderCurrencyTotals(reportData.summary.totalExpense)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-blue-700 dark:text-blue-300">
                          {renderCurrencyTotals(reportData.summary.netIncome)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              /* 일반 월별 보고서 내용 (전월이월, 월말잔액 등) */
              <>
                {/* 💰 전월이월 & 월말잔액 표시 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* 전월 이월금 */}
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-6 border border-purple-200 dark:border-purple-700">
                    <div className="flex items-center mb-3">
                      <div className="p-2 bg-purple-500 rounded-lg">
                        <FaPiggyBank className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-purple-800 dark:text-purple-200 ml-3">
                        📥 {reportData.period.month}월 기초(이월) 잔액
                      </h3>
                    </div>
                    <p className="text-xs text-purple-600 dark:text-purple-400 mb-2">
                      {reportData.period.month === 1
                        ? `연간이월금 (${reportData.period.year - 1}년 → ${reportData.period.year}년)`
                        : `연간이월 + 1~${reportData.period.month - 1}월 순수익 합계`}
                    </p>
                    {renderCurrencyTotals(reportData.previousBalance, 'text-purple-700 dark:text-purple-300 font-bold text-lg')}
                  </div>

                  {/* 월말 잔액 */}
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-6 border border-indigo-200 dark:border-indigo-700">
                    <div className="flex items-center mb-3">
                      <div className="p-2 bg-indigo-500 rounded-lg">
                        <FaCheckCircle className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-indigo-800 dark:text-indigo-200 ml-3">
                        💎 {reportData.period.month}월 말 잔액
                      </h3>
                    </div>
                    <p className="text-xs text-indigo-600 dark:text-indigo-400 mb-2">
                      전월이월 + {reportData.period.month}월 순수익
                    </p>
                    {renderCurrencyTotals(reportData.endingBalance, 'text-indigo-700 dark:text-indigo-300 font-bold text-lg')}
                  </div>
                </div>


                {/* 요약 통계 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 border border-green-200 dark:border-green-700">
                    <div className="flex items-center mb-3">
                      <div className="p-2 bg-green-500 rounded-lg">
                        <FaChartBar className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 ml-3">총 수입</h3>
                    </div>
                    {renderCurrencyTotals(reportData.summary.totalIncome, 'text-green-700 dark:text-green-300 font-medium')}
                  </div>

                  <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-6 border border-red-200 dark:border-red-700">
                    <div className="flex items-center mb-3">
                      <div className="p-2 bg-red-500 rounded-lg">
                        <FaChartBar className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 ml-3">총 지출</h3>
                    </div>
                    {renderCurrencyTotals(reportData.summary.totalExpense, 'text-red-700 dark:text-red-300 font-medium')}
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-700">
                    <div className="flex items-center mb-3">
                      <div className="p-2 bg-blue-500 rounded-lg">
                        <FaCheckCircle className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 ml-3">순손익</h3>
                    </div>
                    {renderCurrencyTotals(reportData.summary.netIncome, 'text-blue-700 dark:text-blue-300 font-bold')}
                  </div>
                </div>

                {/* 수입 분석 */}
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 border border-green-200 dark:border-green-700">
                  <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-4">💰 수입 분석</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <h4 className="font-medium text-green-700 dark:text-green-300 mb-2">📊 십일조</h4>
                      {renderCurrencyTotals(reportData.categoryBreakdown.income.tithe, 'text-green-600 dark:text-green-400')}
                    </div>
                    <div>
                      <h4 className="font-medium text-green-700 dark:text-green-300 mb-2">🙏 감사헌금</h4>
                      {renderCurrencyTotals(reportData.categoryBreakdown.income.thanksgiving, 'text-green-600 dark:text-green-400')}
                    </div>
                    <div>
                      <h4 className="font-medium text-green-700 dark:text-green-300 mb-2">✨ 기타헌금</h4>
                      {renderCurrencyTotals(reportData.categoryBreakdown.income.other, 'text-green-600 dark:text-green-400')}
                    </div>
                  </div>
                </div>

                {/* 지출 분석 */}
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-6 border border-red-200 dark:border-red-700">
                  <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-4">💸 지출 분석</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {Object.entries(reportData.categoryBreakdown.expense).map(([category, totals]) => {
                      const hasAmount = Object.values(totals).some(amount => amount > 0);
                      if (!hasAmount) return null;

                      return (
                        <div key={category}>
                          <h4 className="font-medium text-red-700 dark:text-red-300 mb-2">
                            {EXPENSE_CATEGORY_LABELS[category as keyof typeof EXPENSE_CATEGORY_LABELS]}
                          </h4>
                          {renderCurrencyTotals(totals, 'text-red-600 dark:text-red-400')}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* 상세 거래 내역 */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">📋 상세 거래 내역</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                      <thead className="bg-gray-100 dark:bg-gray-600">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">날짜</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">수입</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">지출</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">순손익</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">작성자</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                        {reportData.records.map((record) => (
                          <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                              {record.date}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-green-600 dark:text-green-400 font-medium">
                              {formatCurrencyTotals(record.incomeTotals)}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-red-600 dark:text-red-400 font-medium">
                              {formatCurrencyTotals(record.expenseTotals)}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-blue-600 dark:text-blue-400 font-bold">
                              {formatCurrencyTotals(record.netTotals)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                              {record.createdBy}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
